import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Layers } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function CommitteesPage() {
  const [committees, setCommittees] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle("National Committees");

  useEffect(() => {
    Promise.all([
      api.get("/content/committees"),
      api.get("/pages/by-slug/national-committees").catch(() => ({ data: null })),
    ]).then(([c, p]) => {
      setCommittees(c.data);
      setPage(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="committees-page">
      {/* Hero */}
      <div className="bg-jci-black relative overflow-hidden">
        {page?.image_url ? (
          <div className="relative h-[300px] md:h-[400px]">
            <img src={page.image_url} alt="National Committees" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-jci-black/60" />
            <div className="absolute inset-0 flex items-center">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">National Committees</h1>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">National Committees</h1>
            </div>
          </div>
        )}
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => <Skeleton key={i} className="h-72 rounded-2xl" />)}
          </div>
        ) : committees.length === 0 ? (
          <div className="text-center py-20">
            <Layers className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">Committees will be added soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="committees-grid">
            {committees.map((c, i) => (
              <Link to={`/committee/${c.slug}`} key={c.id} data-testid={`committee-card-${i}`}>
                <Card className="card-hover border-0 shadow-md overflow-hidden h-full group">
                  {c.image_url && (
                    <div className="overflow-hidden h-48">
                      <img src={c.image_url} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="font-bold text-jci-black text-lg mb-2 group-hover:text-jci-blue transition-colors">{c.name}</h3>
                    {c.description && <p className="text-sm text-slate-500 line-clamp-3">{c.description}</p>}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
